import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Chip, 
  Divider, 
  Paper,
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ComingSoonIcon from '@mui/icons-material/Schedule';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import appConfig from '../config/appConfig';

const ChangelogPage = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 2 }}
        >
          Retour à l'accueil
        </Button>
        <Typography variant="h3" component="h1" gutterBottom>
          Changelog
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {appConfig.appName} v{appConfig.version} - Historique des versions
        </Typography>
      </Box>

      {/* Version actuelle */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, border: '2px solid', borderColor: 'success.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h2" color="success.main" sx={{ fontWeight: 'bold' }}>
            v1.5.0 ✨
          </Typography>
          <Chip label="ACTUELLE" color="success" variant="filled" />
          <Chip label="RÉSUMÉ" color="info" variant="outlined" />
          <Chip label="AUTH" color="error" variant="outlined" />
          <Chip label="ONEDRIVE" color="warning" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          ✨ AMÉLIORATIONS RÉSUMÉ & AUTHENTIFICATION - Génération de résumé sans identification des participants, correction des bugs pour les longues réunions et authentification Azure robuste
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="🎯 Résumé sans identification des participants"
              secondary="Nouvelle possibilité de générer un résumé automatiquement sans passer par l'étape d'identification des intervenants, pour un flux de travail plus rapide"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="📝 Correction des résumés pour réunions longues"
              secondary="Résolution des bugs de génération de résumé pour les réunions de longue durée, avec meilleure gestion du contexte et des tokens"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="🔐 Refresh automatique du token Azure"
              secondary="Nouvelle méthode getValidAzureTokenStrict() qui garantit un token valide pour l'envoi d'email - ne retourne jamais un token expiré"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="📧 Correction de l'envoi d'email automatique"
              secondary="L'envoi d'email après génération de résumé fonctionne maintenant même si l'utilisateur est resté sur la page plus d'une heure sans la rafraîchir"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="📁 Correction OneDrive"
              secondary="Résolution des problèmes d'authentification OneDrive lorsque le token Azure expire pendant une session prolongée"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="⚡ Fiabilité améliorée"
              secondary="Système de retry automatique pour le rafraîchissement des tokens avec fallback SSO intelligent en cas d'échec"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.4.0 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.4.0 🔐
          </Typography>
          <Chip label="SSO SAFARI" color="error" variant="outlined" />
          <Chip label="iOS/macOS" color="warning" variant="outlined" />
          <Chip label="LOGS" color="info" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🔐 REFONTE SSO SAFARI - Système d'authentification SSO complètement refondu pour Safari sur iOS et macOS, système de logs techniques et optimisations performances
        </Typography>

        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="🔐 Refonte complète du système SSO Safari"
              secondary="Réécriture majeure du système d'authentification SSO pour résoudre définitivement les problèmes de compatibilité avec Safari sur iOS (iPhone/iPad) et macOS"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="🍎 Corrections multiples pour iOS/Safari"
              secondary="Gestion optimisée du stockage (localStorage/sessionStorage), validation des données utilisateur, sauvegarde des loginHints et gestion intelligente des tokens Azure avec fallback mémoire"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="📋 Système de logs techniques pour admins"
              secondary="Nouveau service systemLogService pour enregistrer les événements d'authentification, tokens et erreurs système dans Firestore"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText
              primary="⚡ Amélioration du rafraîchissement des tokens"
              secondary="Système de récupération de token SSO silencieux avec loginHint sauvegardé dans multiple storages et stratégies adaptatives iOS/desktop"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.3.0 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.3.0 ✨
          </Typography>
          <Chip label="MULTI-FICHIERS" color="info" variant="outlined" />
          <Chip label="AUTO-FILL" color="secondary" variant="outlined" />
          <Chip label="UX" color="warning" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          ✨ CRÉATION DE RÉUNION RÉVOLUTIONNÉE - Support multi-fichiers audio, pré-remplissage automatique et ajout automatique des participants
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎵 Support multi-fichiers audio"
              secondary="Possibilité d'ajouter plusieurs fichiers audio en une fois lors de la création d'une réunion, avec réorganisation par glisser-déposer et indicateurs visuels"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Pré-remplissage automatique intelligent"
              secondary="Extraction automatique du titre (nom du fichier sans extension) et de la date (dernière modification) pour pré-remplir les champs si vides"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔄 Réorganisation du flux de création"
              secondary="Les fichiers sont maintenant ajoutés en étape 1, permettant le pré-remplissage automatique des informations en étape 2"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="👤 Ajout automatique de l'utilisateur connecté"
              secondary="L'utilisateur qui crée la réunion est automatiquement ajouté comme participant avec son nom et poste récupérés du profil Azure/Firebase"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📁 Compatibilité OneDrive étendue"
              secondary="Support du pré-remplissage automatique pour les fichiers importés depuis OneDrive avec gestion des propriétés lastModifiedDateTime"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Validation adaptative"
              secondary="Mise à jour de la logique de validation pour correspondre au nouvel ordre des étapes (Fichiers → Informations → Confirmation)"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔥 Transcription multi-fichiers automatique"
              secondary="Démarrage automatique de la transcription pour tous les fichiers audio avec gestion intelligente mono/multi-fichiers"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.2.0 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.2.0 🚀
          </Typography>
          <Chip label="SSO" color="info" variant="outlined" />
          <Chip label="IA" color="secondary" variant="outlined" />
          <Chip label="UX" color="warning" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🚀 AUTHENTIFICATION SSO & IA - Nouveau système d'authentification SSO, assistant IA pour modèles de réunion, améliorations notifications et OneDrive
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔐 Nouveau système d'authentification SSO"
              secondary="Authentification Single Sign-On intégrée pour résoudre définitivement les problèmes OneDrive et améliorer la sécurité de connexion"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🤖 Assistant IA pour modèles de réunion"
              secondary="Nouvelle fonctionnalité d'aide à la rédaction de prompts pour les modèles de réunion grâce à l'intelligence artificielle"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔔 Système de notifications amélioré"
              secondary="Masquage automatique des notifications après 2 secondes, suppression des notifications de refresh token, et bouton 'Supprimer tout'"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📁 OneDrive optimisé"
              secondary="Ajout du bouton refresh et affichage des dates de modification pour faciliter la sélection et l'import des fichiers"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Stabilité et performance"
              secondary="Corrections de bugs d'authentification, amélioration de la gestion des tokens et optimisation de l'expérience utilisateur"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.1.2 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.1.2 ⚡
          </Typography>
          <Chip label="PERFORMANCE" color="info" variant="outlined" />
          <Chip label="ARCHITECTURE" color="secondary" variant="outlined" />
          <Chip label="WEBHOOKS" color="warning" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          ⚡ RÉVOLUTION ARCHITECTURE - Remplacement du polling par les webhooks AssemblyAI pour des performances optimales et une réactivité instantanée
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔔 Architecture webhook temps réel"
              secondary="AssemblyAI notifie automatiquement notre système dès qu'une transcription est terminée, éliminant complètement le besoin de polling répétitif"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Performance drastiquement améliorée"
              secondary="Réduction de 90% des appels API et 60% du temps d'exécution des fonctions Firebase grâce à la suppression du système de polling toutes les 5 secondes"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔒 Sécurité webhook renforcée"
              secondary="Chaque transcription génère un secret unique, vérification des en-têtes d'authentification et validation des IPs source AssemblyAI (US/EU)"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🚀 Notifications instantanées"
              secondary="Les utilisateurs sont notifiés immédiatement quand leur transcription est prête, sans délai d'attente du prochain cycle de polling"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="💰 Réduction significative des coûts"
              secondary="Diminution drastique du nombre d'invocations Firebase Functions et d'appels API, optimisant les coûts d'infrastructure"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🛠️ Infrastructure robuste"
              secondary="Nouvelle fonction assemblyAIWebhook avec gestion d'erreur complète, retry automatique et fallback gracieux pour une fiabilité maximale"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🧹 Code obsolète supprimé"
              secondary="Suppression complète du système de polling et de l'ancienne fonction startTranscription pour un code plus propre et maintenable"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.1.1 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'text.secondary', background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
            v1.1.1 🔧
          </Typography>
          <Chip label="UX/UI" color="default" variant="outlined" />
          <Chip label="ADMIN" color="default" variant="outlined" />
          <Chip label="BUGFIX" color="default" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🔧 AMÉLIORATIONS UX & ADMINISTRATION - Système de notifications, refonte admin, corrections d'affichage et optimisations export Word
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔔 Système de notifications complet"
              secondary="Nouveau système de notifications en temps réel avec centre de notifications, indicateurs visuels et gestion des alertes pour améliorer l'expérience utilisateur"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="👑 Page d'administration refaite"
              secondary="Interface d'administration complètement redessinée avec design moderne, navigation améliorée et gestion optimisée des paramètres système"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🐛 Corrections d'affichage diverses"
              secondary="Résolution de multiples bugs d'affichage pour une interface plus stable et cohérente sur tous les navigateurs et appareils"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📧 UX d'envoi d'email améliorée"
              secondary="Refonte de l'expérience utilisateur pour l'envoi d'emails avec interface plus intuitive, feedback visuel amélioré et processus simplifié"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📄 Refonte du modèle Word d'export"
              secondary="Nouveau template Word professionnel avec mise en page optimisée, styles modernisés et structure documentaire améliorée pour les exports"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🧹 Nettoyage du code HomePage"
              secondary="Suppression du panneau de test des notifications de la page d'accueil pour une interface plus épurée en production"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.1.0 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.1.0 ✨
          </Typography>
          <Chip label="IA" color="info" variant="outlined" />
          <Chip label="FEATURE" color="secondary" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🤖 AIDE IA POUR GÉNÉRATION DE PROMPTS - Nouvelle fonctionnalité majeure d'assistance intelligente pour créer des prompts de résumé personnalisés
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🤖 Assistance IA pour génération de prompts"
              secondary="Nouvelle fonctionnalité permettant aux utilisateurs de discuter avec l'IA pour générer automatiquement des prompts de résumé optimisés et personnalisés"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="💬 Chat interactif avec IA experte"
              secondary="Interface de chat moderne permettant de décrire ses besoins en langage naturel, l'IA GPT-4o génère ensuite un prompt professionnel adapté"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Intégration transparente dans les dialogues"
              secondary="Boutons '🤖 Générer avec l'IA' ajoutés dans les dialogues de création et édition de modèles pour un accès direct à l'assistance intelligente"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Application automatique des prompts générés"
              secondary="Bouton 'Utiliser ce prompt' qui apparaît automatiquement quand l'IA propose un prompt, permettant de l'appliquer directement dans le formulaire"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="✨ Animation d'aide contextuelle"
              secondary="Icône IA animée avec effet machine à écrire affichant 'Besoin d'aide pour générer un prompt parfait ?' pour guider intuitivement l'utilisateur"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎨 Interface chat moderne glassmorphism"
              secondary="Design chat premium avec bulles de messages différenciées, rendu markdown complet, auto-scroll et animations fluides pour une expérience utilisateur optimale"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🧠 IA experte spécialisée résumés de réunions"
              secondary="System prompt optimisé pour créer des prompts de résumé professionnel avec détection intelligente et extraction automatique du contenu généré"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.0.9 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.0.9 🧹
          </Typography>
          <Chip label="MAINTENANCE" color="secondary" variant="outlined" />
          <Chip label="LOGS" color="info" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🧹 NETTOYAGE CONTINU - Suppression des derniers logs de debug pour une console parfaitement épurée
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔇 Suppression du log 'Pas de permission pour lire les infos du créateur'"
              secondary="Élimination du dernier message informatif dans UniversalCard.js lors des erreurs de permissions pour une console silencieuse"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="✨ Console développeur parfaitement épurée"
              secondary="Atteinte de l'objectif de zéro log non-critique en production pour une expérience développeur optimale"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Gestion d'erreur gracieuse maintenue"
              secondary="Conservation de la logique de fallback sur l'email du créateur sans polluer la console avec des messages informatifs"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🚀 Performance de debug améliorée"
              secondary="Réduction maximale du bruit en console permettant aux développeurs de se concentrer uniquement sur les vraies erreurs"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔧 Finalisation du nettoyage technique"
              secondary="Accomplissement de la mission de nettoyage initiée en v1.0.8 avec suppression complète des logs de debug non-essentiels"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.0.8 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.0.8 ⚡
          </Typography>
          <Chip label="PERFORMANCE" color="info" variant="outlined" />
          <Chip label="MAINTENANCE" color="secondary" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          ⚡ OPTIMISATION PERFORMANCE - Nettoyage des logs de debug et amélioration des performances système
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🧹 Nettoyage complet des logs de debug"
              secondary="Suppression de tous les console.log du système d'authentification pour améliorer les performances et réduire le bruit en console"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Optimisation des performances d'authentification"
              secondary="Réduction de la charge CPU en supprimant les logs intensifs du rafraîchissement automatique des tokens et de la surveillance réseau"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔄 Amélioration du système de rafraîchissement"
              secondary="Processus de rafraîchissement automatique des tokens plus silencieux et efficient sans impact sur l'expérience utilisateur"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🌐 Surveillance réseau optimisée"
              secondary="Monitoring de la connexion réseau sans logs verbeux tout en maintenant la robustesse du système de reconnexion automatique"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🚀 Console développeur épurée"
              secondary="Expérience de développement améliorée avec une console propre facilitant le debug des vraies erreurs métier"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Mode production optimisé"
              secondary="Application plus légère et performante en production avec réduction significative des opérations de logging inutiles"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.0.7 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.0.7 ✨
          </Typography>
          <Chip label="UX/UI" color="info" variant="outlined" />
          <Chip label="DESIGN" color="secondary" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          ✨ REFONTE UI DIALOGUE RÉSUMÉ - Consolidation et amélioration UX avec design liquid glass moderne
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔄 Consolidation des dialogues de résumé"
              secondary="Fusion des deux dialogues séparés (identification des intervenants et génération) en un seul dialogue unifié avec navigation par étapes"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="✨ Design liquid glass moderne et responsive"
              secondary="Application d'un design glassmorphism avec cartes compactes, effets backdrop-filter et grille responsive 7/12 pour modèles et 5/12 pour l'aperçu"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📱 Interface adaptative et optimisée"
              secondary="Correction des problèmes de titres superposés, boutons non fixés et amélioration de l'affichage des modèles avec cartes hauteur limitée et troncature intelligente"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Navigation par étapes intelligente"
              secondary="Point d'entrée adaptatif : étape 0 si identification nécessaire, étape 1 sinon, avec actions contextuelles par étape (Annuler/Ignorer/Suivant puis Retour/Générer)"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🖥️ Aperçu prompt style terminal"
              secondary="Affichage du prompt sélectionné dans un conteneur style terminal sombre à droite (desktop) ou en bas (mobile) avec scrollbar personnalisée"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🧹 Refactoring et nettoyage du code"
              secondary="Suppression des anciens dialogues redondants, consolidation de la gestion d'état et optimisation du flux utilisateur pour une architecture plus maintenable"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎨 Hiérarchie visuelle améliorée"
              secondary="Cartes de modèles compactes avec expansion au survol/sélection, typographie optimisée et espacement cohérent pour une meilleure lisibilité"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.0.6 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'error.main', background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="error.main" sx={{ fontWeight: 'bold' }}>
            v1.0.6 🔐
          </Typography>
          <Chip label="SÉCURITÉ" color="error" variant="outlined" />
          <Chip label="PERFORMANCE" color="info" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🔐 OPTIMISATIONS SÉCURITÉ & PERFORMANCE - Simplification des règles Firestore et stabilisation du ChatBot
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔐 Simplification complète des règles Firestore"
              secondary="Refonte majeure des règles de sécurité de la base de données pour éliminer les erreurs de permissions complexes tout en maintenant la sécurité"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Élimination des erreurs 'Missing or insufficient permissions'"
              secondary="Résolution définitive des problèmes d'accès pour les utilisateurs non-admin grâce à des règles simplifiées et un filtrage côté client"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🤖 Stabilisation du ChatBot IA"
              secondary="Correction des boucles d'initialisation du chatbot avec système de protection anti-redémarrage et gestion optimisée des dépendances"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🛡️ Sécurité côté client renforcée"
              secondary="Implémentation de filtres côté client pour maintenir la sécurité des données tout en simplifiant les règles serveur complexes"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📈 Optimisation des listeners Firestore"
              secondary="Amélioration des performances des onSnapshot listeners avec gestion d'erreur gracieuse et prévention des échecs de requêtes complexes"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔄 Gestion robuste des états de chargement"
              secondary="Système amélioré de protection contre les changements d'état après démontage des composants pour une expérience utilisateur stable"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🏗️ Infrastructure de base de données optimisée"
              secondary="Déploiement des nouvelles règles Firestore simplifiées sur toutes les collections (meetings, users, models, messages, transcriptions, summaries)"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.0.5 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'warning.main', background: 'linear-gradient(135deg, #fff8e1 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="warning.main" sx={{ fontWeight: 'bold' }}>
            v1.0.5 📧
          </Typography>
          <Chip label="AUTO EMAIL" color="warning" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          📧 AUTOMATISATION EMAIL - Envoi automatique des résumés par email
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📧 Envoi automatique d'email des résumés"
              secondary="Dès qu'un résumé est généré avec succès, l'utilisateur reçoit automatiquement un email professionnel contenant le résumé complet"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎨 Template HTML moderne et responsive"
              secondary="Email avec design glassmorphism, informations complètes de la réunion, participants, contexte et statistiques de coût"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔐 Authentification sécurisée Microsoft Graph"
              secondary="Utilise le token Azure de l'utilisateur pour envoyer l'email depuis sa propre boîte mail avec authentification robuste"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🛡️ Gestion d'erreur gracieuse"
              secondary="L'échec d'envoi d'email n'interrompt pas la génération du résumé, garantissant la disponibilité du service principal"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="💰 Informations de coût incluses"
              secondary="Chaque email contient les détails du modèle IA utilisé, nombre de tokens consommés et coût estimé en euros/dollars"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Infrastructure optimisée"
              secondary="Réutilise la fonction sendEmail existante pour garantir la cohérence et la fiabilité des envois"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.0.4 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'primary.main', background: 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="primary.main" sx={{ fontWeight: 'bold' }}>
            v1.0.4 🚀
          </Typography>
          <Chip label="UX/UI" color="info" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🚀 FONCTIONNALITÉ MAJEURE - ChatBot IA intelligent et améliorations utilisateur
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🤖 ChatBot IA pour l'analyse de réunions"
              secondary="Nouveau chatbot intelligent capable d'analyser vos transcriptions de réunions et de répondre à vos questions de manière contextuelle avec GPT-4.1"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="💬 Interface conversationnelle moderne"
              secondary="Chat fluide avec design glassmorphism, messages typés en temps réel, gestion des erreurs et expérience utilisateur optimisée"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Analyse contextuelle intelligente"
              secondary="Le chatbot comprend le contexte de chaque réunion spécifique et peut générer des insights personnalisés, extraire des informations clés et répondre aux questions"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Intégration transparente"
              secondary="Accessible directement depuis la page de détails de chaque réunion avec une interface moderne et responsive"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="👤 Affichage des noms d'utilisateurs amélioré"
              secondary="Dans la liste des modèles de prompts, affichage du prénom et nom complet des utilisateurs au lieu de l'ID technique Azure"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Navigation intuitive depuis la homepage"
              secondary="Clic sur la carte 'Mes Réunions' de la page d'accueil redirige automatiquement vers la liste des réunions pour un accès rapide"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔧 Chargement intelligent des profils utilisateur"
              secondary="Système de cache des profils utilisateur pour éviter les appels API répétés et améliorer les performances"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Version précédente v1.0.3 */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, border: '1px solid', borderColor: 'info.main', background: 'linear-gradient(135deg, #f0f8ff 0%, #ffffff 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h2" color="info.main" sx={{ fontWeight: 'bold' }}>
            v1.0.3 🎯
          </Typography>
          <Chip label="UX/UI" color="info" variant="outlined" />
        </Box>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          🎯 PERFECTIONNEMENT UX - Corrections visuelles, navigation optimisée et uniformisation des interfaces
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📅 Correction de l'icône de date"
              secondary="Remplacement de l'icône horloge par une icône calendrier pour le bloc Date, améliorant la cohérence visuelle"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎨 Correction des couleurs de statut"
              secondary="Chips de statut avec texte blanc pour une meilleure lisibilité sur les fonds colorés transparents"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔄 Correction de la navigation entre onglets"
              secondary="Résolution du bug empêchant le retour sur l'onglet Transcription après le basculement automatique vers Résumé"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="✏️ Correction du lien 'Modifier la réunion'"
              secondary="Ouverture de la dialog EditMeetingDialog moderne au lieu d'une navigation vers une page inexistante"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📐 Uniformisation des blocs d'informations"
              secondary="Hauteur minimale fixe (180px) sur tous les blocs de la HeroSection pour un rendu visuel parfaitement aligné"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon fontSize="small" sx={{ color: 'info.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="⚡ Gestion intelligente du premier chargement"
              secondary="Système d'état isInitialLoad pour éviter les conflits de navigation et améliorer l'expérience utilisateur"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Versions précédentes */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Versions précédentes
        </Typography>

        {/* Version v1.0.2 - MODERNISATION DESIGN */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'secondary.main', background: 'linear-gradient(135deg, #faf0ff 0%, #ffffff 100%)' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
            v1.0.2 🎨
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            🎨 MODERNISATION COMPLÈTE - Design glassmorphism unifié, interface mobile optimisée et expérience utilisateur premium
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🪟 Modernisation complète des dialogs"
                secondary="Application du design glassmorphism cohérent sur tous les dialogs de l'application : nouveaux modèles, édition, création de réunions, partage et emails"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📱 Optimisation mobile avancée"
                secondary="Support fullScreen mobile parfait avec borderRadius 0, hauteur 100vh et typography responsive (14px mobile, 16px desktop)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="✨ En-têtes glassmorphism premium"
                secondary="Dégradés bleu indigo harmonisés, patterns SVG subtils en arrière-plan et effets backdrop-filter sur tous les dialogs"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Corrections ciblées mobile"
                secondary="Ajustement des hauteurs d'en-tête mobile (py: 6.5 pour les dialogs complexes), typography adaptative et navigation optimisée"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🔧 Corrections techniques"
                secondary="Import CloseIcon ajouté, corrections d'erreurs de syntaxe, et harmonisation complète du code sur tous les dialogs"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Design system unifié"
                secondary="Transitions cubic-bezier fluides, boxShadow modernes, et cohérence visuelle parfaite entre tous les composants dialog"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'secondary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Dialogs MeetingDetailsPage modernisés"
                secondary="Partage de réunion, envoi d'email, génération de résumé, identification des locuteurs et confirmations avec design glassmorphism"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Version v1.0.0 - LANCEMENT OFFICIEL */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'primary.main', background: 'linear-gradient(135deg, #f8faff 0%, #ffffff 100%)' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            v1.0.0 🎉
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            🚀 LANCEMENT OFFICIEL - Homepage moderne, dashboard intelligent et expérience utilisateur premium
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🏠 Homepage complètement refaite"
                secondary="Nouvelle page d'accueil moderne avec design glassmorphism, statistiques temps réel et dashboard intelligent remplaçant l'ancienne interface"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📊 Statistiques utilisateur en temps réel"
                secondary="Cartes de statistiques avec compteurs animés : Mes Réunions, Transcriptions effectuées, Résumés générés avec écoute temps réel Firestore"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📋 Section dernières réunions intelligente"
                secondary="Affichage des 5 dernières réunions avec design exact de MeetingsListPage : bandeaux colorés, chips de statut, dates formatées et navigation directe"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎥 Vidéo tutoriel intégrée"
                secondary="Vidéo Loom d'assistance utilisateur intégrée directement dans la homepage avec design moderne et responsive"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📈 Section progression dynamique"
                secondary="Calculs intelligents du temps économisé, précision IA et statistiques de lecture rapide avec animations CountUp"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Design harmonisé et compact"
                secondary="AppBar et en-tête parfaitement harmonisés, hauteur optimisée (20vh), effets géométriques modernes et espacement équilibré"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📄 Page À propos créée"
                secondary="Transformation de l'ancienne homepage en page dédiée 'À propos' accessible depuis le menu de navigation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Dialog de création intégrée"
                secondary="Bouton 'Nouvelle Réunion' fonctionnel ouvrant directement la dialog de création depuis la homepage"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📜 Footer professionnel"
                secondary="Disclaimer avec copyright, développeur (Matthieu SIMAR) et version dynamique récupérée depuis appConfig.js"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🔗 Accès changelog dans le profil"
                secondary="Nouveau lien 'Changelog' dans le menu de profil utilisateur pour suivre les évolutions de l'application"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="✨ Expérience utilisateur premium"
                secondary="Animations fluides, transitions modernes, feedback visuel optimal et design cohérent sur toute l'application"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.8 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.8-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Design moderne avec glassmorphism et expérience utilisateur premium
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Refonte complète du design du sidebar"
                secondary="Application du glassmorphism avec effets de transparence, dégradés subtils et animations fluides pour une interface premium"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="✨ Micro-interactions et animations avancées"
                secondary="Effets de survol sophistiqués, animations d'échelle, rotations et transitions avec courbes de Bézier pour une expérience immersive"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Animations de sélection dynamiques"
                secondary="Effet de pulsation avec ombres et mise à l'échelle sur les onglets sélectionnés pour un feedback visuel optimal"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🔧 Corrections d'affichage AppBar"
                secondary="Résolution du problème de hauteur complète de l'AppBar avec positionnement fixe à 70px et ajustement du z-index"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Cohérence des couleurs et thème unifié"
                secondary="Harmonisation des couleurs avec thème bleu cohérent, ajustement des tailles de boutons et design épuré"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🪟 Design homogène des dialogs"
                secondary="Application du style glassmorphism aux dialogs modaux avec effets de flou, transparence et transitions cohérentes"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Optimisations des performances visuelles"
                secondary="Utilisation de backdrop-filter, transitions GPU et animations optimisées pour une fluidité maximale"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.7 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.7-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Modèles de réunion et gestion de templates avancée
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Modèles de réunion"
                secondary="Nouvelle fonctionnalité complète pour créer et gérer des modèles de réunion avec contexte et participants pré-définis"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📄 Gestion des templates"
                secondary="Page dédiée pour créer, modifier, dupliquer et supprimer les modèles de réunion avec interface cards moderne"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🔄 Conversion réunion vers modèle"
                secondary="Option 'Créer un modèle' dans les menus des réunions existantes pour transformer rapidement une réunion en template"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Sélecteur de modèle"
                secondary="Intégration dans NewMeetingDialog pour pré-remplir titre, contexte et participants à partir d'un modèle existant"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Interface cohérente"
                secondary="Design moderne avec glassmorphism, gestion des permissions et navigation intuitive vers /models/meeting"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.6 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.6-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Expérience utilisateur optimisée et permissions de partage améliorées
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📝 Dialog de création de réunion"
                secondary="Transformation de la création de réunion en dialog modal pour une expérience plus fluide, accessible depuis la liste et le menu de navigation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Chargement fluide des transcriptions"
                secondary="Suppression du message d'erreur temporaire pendant le chargement des données de transcription"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎵 Indicateur de chargement audio"
                secondary="Icône de chargement pendant le buffering audio, puis basculement automatique sur pause une fois la lecture lancée"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📊 Navigation intelligente des onglets"
                secondary="Basculement automatique sur l'onglet résumé si un résumé est disponible lors de l'ouverture d'une réunion"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🔐 Accès aux résumés partagés"
                secondary="Correction du problème empêchant la visualisation des résumés dans les réunions partagées"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Permissions de partage cohérentes"
                secondary="Logique unifiée pour l'accès aux transcriptions et résumés selon les droits de partage"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.5 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.5-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Interface transcription premium et contrôles audio avancés
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Design glassmorphism premium"
                secondary="Nouveau header avec effet glassmorphism, dégradés subtils et interface moderne inspirée 2025"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎵 Lecture audio intégrée"
                secondary="Boutons play/pause pour écouter chaque segment de la transcription avec gestion intelligente"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="👥 Attribution des participants"
                secondary="Menu contextuel Material-UI pour identifier facilement les locuteurs avec support mobile optimisé"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📱 UX mobile perfectionnée"
                secondary="Correction des débordements, scroll intelligent et interface adaptative sans défilement horizontal"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Navigation optimisée"
                secondary="Bouton scroll-to-top intelligent qui cible le bon conteneur selon le contexte"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.4 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.4-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Bottom navigation mobile et UX améliorée
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📱 Bottom Navigation Mobile"
                secondary="Ajout d'un menu de navigation en bas sur mobile pour les pages réunions et modèles"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Navigation adaptative"
                secondary="Masquage automatique des tabs sur mobile, remplacés par le bottom navigation"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Navigation fluide"
                secondary="Suppression des rechargements de page lors des changements d'onglets"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📐 UX mobile optimisée"
                secondary="Interface entièrement repensée pour une meilleure ergonomie sur appareils tactiles"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.3 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.3-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Optimisations mobile avancées et navigation améliorée
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📱 Navigation mobile optimisée"
                secondary="Onglets scrollables sur mobile pour une meilleure navigation sur petits écrans"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📐 Gestion du padding améliorée"
                secondary="Correction des problèmes de largeur sur iPhone avec padding horizontal optimisé"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Cohérence design"
                secondary="Harmonisation complète du layout entre les pages modèles et réunions"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📱 Responsive design perfectionné"
                secondary="Élimination du défilement horizontal indésirable sur tous les appareils mobiles"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.2 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.2-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Optimisations mobile et améliorations UX
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📱 Optimisations mobile complètes"
                secondary="Interface entièrement optimisée pour mobile avec boutons adaptatifs et layout responsive"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Boutons intelligents"
                secondary="Texte adaptatif sur les boutons (ex: 'Nouveau' sur mobile, 'Nouvelle réunion' sur desktop)"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📐 Layout flexible"
                secondary="Prévention du chevauchement d'éléments avec flexwrap et espacement adaptatif"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="✨ Design cohérent"
                secondary="Harmonisation du design entre les pages de modèles et de réunions"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.1 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.1-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Nouveau design des réunions et mise à jour temps réel
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎨 Nouveau design des réunions"
                secondary="Interface redessinée pour la liste des réunions avec cartes compactes et bandeau coloré selon le statut"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="⚡ Mise à jour temps réel"
                secondary="Suivi en temps réel des transcriptions et résumés sans rechargement de page grâce à onSnapshot"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📊 Indicateurs visuels améliorés"
                secondary="Chips de statut avec icônes, barre de progression pour les transcriptions en cours et bandeau coloré"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🔄 Synchronisation automatique"
                secondary="Les changements d'état des transcriptions et résumés s'affichent automatiquement pour tous les utilisateurs"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.4.0 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.4.0-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Modèles de prompts personnalisés et gestion des permissions
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🎯 Modèles de prompts personnalisés"
                secondary="Création et gestion de modèles de prompts personnalisés pour les résumés avec permissions admin"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="👥 Gestion des permissions avancée"
                secondary="Les administrateurs peuvent modifier tous les modèles de prompt avec indicateurs visuels"
              />
            </ListItem>
          </List>
        </Paper>

        {/* v0.3.3 */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            v0.3.3-beta
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Envoi par email et export Word optimisé
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📧 Envoi par email"
                secondary="Envoi séparé du résumé et de la transcription par email avec mise en forme professionnelle"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="📄 Export Word optimisé"
                secondary="Design moderne avec polices élégantes, icônes et support complet des tableaux"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText 
                primary="🔐 Corrections authentification"
                secondary="Résolution des problèmes de tokens Azure et optimisations mobiles"
              />
            </ListItem>
          </List>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Fonctionnalités à venir */}
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderLeft: '4px solid', borderColor: 'warning.main' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'warning.main' }}>
          Fonctionnalités à venir
        </Typography>
        
        <List dense>
          <ListItem>
            <ListItemIcon>
              <ComingSoonIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📈 Statistiques avancées"
              secondary="Statistiques d'utilisation détaillées par utilisateur et organisation avec dashboard analytics"
            />
            <Chip label="Bientôt" size="small" color="warning" variant="outlined" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ComingSoonIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🔍 Recherche intelligente"
              secondary="Recherche sémantique dans les transcriptions et résumés avec IA et filtres avancés"
            />
            <Chip label="Bientôt" size="small" color="warning" variant="outlined" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ComingSoonIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="🎯 Résumés personnalisés via chatbot"
              secondary="Génération de nouveaux résumés avec prompts personnalisés directement depuis le chatbot"
            />
            <Chip label="Bientôt" size="small" color="warning" variant="outlined" />
          </ListItem>
          {/* <ListItem>
            <ListItemIcon>
              <ComingSoonIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText 
              primary="📊 Tableaux de bord collaboratifs"
              secondary="Espaces de travail partagés avec suivi de l'activité d'équipe et métriques de productivité"
            />
            <Chip label="Bientôt" size="small" color="warning" variant="outlined" />
          </ListItem> */}
        </List>
      </Paper>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {appConfig.appName} v{appConfig.version}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Développé par {appConfig.developer}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {appConfig.copyright}
        </Typography>
      </Box>
    </Container>
  );
};

export default ChangelogPage; 